import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { HttpStatusCode } from "@angular/common/http";
import { CuratorSavedReportService } from "../../../curator/report/saved-report/service/curator-saved-report.service";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { Subject, switchMap, throwError } from "rxjs";
import { ExporterService } from "../../services/exporter/exporter.service";
import { UserAuthorityService } from "../../services/user-authority/service/user-authority.service";
import { JwtService } from "../../services/auth/jwt.service";
import { UserAuthorityEnum } from "../../services/user-authority/model/user-authority-enum.model";
import { isTruthyValue } from "../../utils/utils";
import { ForbiddenComponent } from "../forbidden/forbidden.component";
import { Button } from "primeng/button";

@Component({
	selector: "app-scheduled-report",
	imports: [ForbiddenComponent, Button, RouterLink],
	templateUrl: "./scheduled-report.component.html"
})
export class ScheduledReportComponent implements OnInit, OnDestroy {
	private readonly jwtService = inject(JwtService);
	private readonly curatorSavedReportService = inject(CuratorSavedReportService);
	private readonly userAuthorityService = inject(UserAuthorityService);

	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);

	hasGenericError: boolean = false;
	hasForbiddenError: boolean = false;

	private savedReportId?: number;
	private returnUrl?: string;
	private readonly destroyed$ = new Subject<void>();

	ngOnInit() {
		const tokenId = this.jwtService.getCurrentTokenId();

		if (!tokenId) {
			this.forceLogout(this.router.url);
			return;
		}

		const savedReportIdString = this.route.snapshot.paramMap.get("savedReportId");

		if (!savedReportIdString?.trim()) {
			this.hasGenericError = true;
			return;
		}

		this.savedReportId = Number(savedReportIdString);

		this.userAuthorityService
			.getAuthorities()
			.pipe(
				switchMap(authorities => {
					this.returnUrl = authorities.includes(UserAuthorityEnum.GLOBAL_BACKOFFICE)
						? "/admin/saved-report"
						: "/saved-report";

					const hasSavedReportsAuthority = authorities.includes(
						UserAuthorityEnum.MODULE_SAVED_REPORTS
					);

					if (!hasSavedReportsAuthority) {
						return throwError(() => new Error("User has not authority to download report."));
					}

					if (isTruthyValue(this.savedReportId)) {
						return this.curatorSavedReportService.getScheduledReport(this.savedReportId);
					}

					return throwError(() => new Error("Invalid report id given"));
				})
			)
			.subscribe({
				next: scheduledReport => {
					this.hasGenericError = false;
					ExporterService.saveAsCSV(scheduledReport, `scheduled-report-${this.savedReportId}`);
					void this.router.navigate([this.returnUrl]);
				},
				error: error => {
					console.error("Error while downloading scheduled report", error);
					this.hasGenericError = true;
					this.hasForbiddenError = error.status === HttpStatusCode.Forbidden;
				}
			});
	}

	ngOnDestroy() {
		this.destroyed$.next();
		this.destroyed$.complete();
	}

	private forceLogout(returnUrl: string) {
		this.jwtService.removeToken();
		void this.router.navigate(["/login"], { queryParams: { returnUrl } });
	}
}
